/**
 * Landing Page View — Vista pública de una landing page.
 * Ruta pública: /l/:slug (storefront del tenant)
 *
 * Renderiza las secciones configuradas, el formulario de captura,
 * y trackea UTM + visitas.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────
interface LandingSection {
  section_type: string;
  display_order: number;
  settings: Record<string, unknown>;
}

interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

interface LandingPageData {
  id: string;
  tenant_id: string;
  title: string;
  slug: string;
  meta_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  sections_json: LandingSection[];
  settings_json: Record<string, unknown>;
  custom_css: string | null;
  form_enabled: boolean;
  form_fields: FormField[];
  form_submit_label: string;
  form_success_message: string;
  form_redirect_url: string | null;
  default_utm_source: string | null;
  default_utm_medium: string | null;
  default_utm_campaign: string | null;
}

// ─── Supabase Edge Function URL ───────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export default function LandingPageView() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Form state
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // UTM from URL params (or defaults)
  const utm = useMemo(() => ({
    source: searchParams.get('utm_source') || null,
    medium: searchParams.get('utm_medium') || null,
    campaign: searchParams.get('utm_campaign') || null,
    term: searchParams.get('utm_term') || null,
    content: searchParams.get('utm_content') || null,
  }), [searchParams]);

  // ─── Load page ─────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;

    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('landing_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .limit(1)
        .maybeSingle();

      if (data) {
        setPage(data as unknown as LandingPageData);

        // Set page title
        document.title = data.meta_title || data.title || 'Landing Page';
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc && data.meta_description) {
          metaDesc.setAttribute('content', data.meta_description);
        }

        // Track view (fire-and-forget)
        try {
          await supabase.rpc('increment_landing_page_view', {
            p_tenant_id: data.tenant_id,
            p_landing_page_id: data.id,
          });
        } catch { /* silent */ }
      } else {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [slug]);

  // ─── Handle form submit ────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!page || submitting) return;

    setFormError(null);
    setSubmitting(true);

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/landing-form-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          landing_page_id: page.id,
          form_data: formData,
          utm_source: utm.source || page.default_utm_source,
          utm_medium: utm.medium || page.default_utm_medium,
          utm_campaign: utm.campaign || page.default_utm_campaign,
          utm_term: utm.term,
          utm_content: utm.content,
          referrer_url: document.referrer || null,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.missing) {
          setFormError(`Campos requeridos: ${result.missing.join(', ')}`);
        } else {
          setFormError(result.error || 'Error al enviar');
        }
        return;
      }

      setSubmitted(true);

      // Redirect if configured
      if (page.form_redirect_url) {
        window.location.href = page.form_redirect_url;
      }
    } catch {
      setFormError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="landing-loading">
        <div className="landing-spinner" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="landing-not-found">
        <h1>Página no encontrada</h1>
        <p>La página que buscas no existe o no está publicada.</p>
      </div>
    );
  }

  return (
    <div className="landing-page">
      {page.custom_css && <style>{page.custom_css}</style>}

      {/* Render sections */}
      {page.sections_json
        .sort((a, b) => a.display_order - b.display_order)
        .map((section, idx) => (
          <SectionRenderer key={`${section.section_type}-${idx}`} section={section} />
        ))}

      {/* Form */}
      {page.form_enabled && !submitted && (
        <section className="landing-form-section">
          <div className="landing-container">
            <form onSubmit={handleSubmit} className="landing-form">
              {page.form_fields.map((field) => (
                <div key={field.id} className="landing-form-field">
                  <label htmlFor={`lf-${field.id}`}>
                    {field.label}
                    {field.required && <span className="landing-required">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      id={`lf-${field.id}`}
                      value={formData[field.id] ?? ''}
                      onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                      placeholder={field.placeholder}
                      required={field.required}
                      rows={3}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      id={`lf-${field.id}`}
                      value={formData[field.id] ?? ''}
                      onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                      required={field.required}
                    >
                      <option value="">{field.placeholder || 'Seleccionar...'}</option>
                      {(field.options ?? []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`lf-${field.id}`}
                      type={field.type}
                      value={formData[field.id] ?? ''}
                      onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  )}
                </div>
              ))}

              {formError && <div className="landing-form-error">{formError}</div>}

              <button type="submit" className="landing-form-submit" disabled={submitting}>
                {submitting ? 'Enviando...' : page.form_submit_label}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* Success message */}
      {submitted && (
        <section className="landing-form-success">
          <div className="landing-container">
            <div className="landing-success-message">
              <h2>&#10003;</h2>
              <p>{page.form_success_message}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ─── Section Renderer ────────────────────────────────────
function SectionRenderer({ section }: { section: LandingSection }) {
  const s = section.settings;
  const heading = String(s.heading ?? '');
  const subheading = String(s.subheading ?? '');
  const content = String(s.content ?? '');

  switch (section.section_type) {
    case 'hero':
      return (
        <section
          className="landing-hero"
          style={{
            backgroundImage: s.background_image_url ? `url(${s.background_image_url})` : undefined,
          }}
        >
          <div className="landing-hero-overlay" />
          <div className="landing-hero-content">
            {heading && <h1>{heading}</h1>}
            {subheading && <p className="landing-hero-sub">{subheading}</p>}
            {s.button_label && (
              <a href={String(s.button_url ?? '#form')} className="landing-cta-button">
                {String(s.button_label)}
              </a>
            )}
          </div>
        </section>
      );

    case 'rich-text':
      return (
        <section className="landing-rich-text">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            {content && <div dangerouslySetInnerHTML={{ __html: content }} />}
          </div>
        </section>
      );

    case 'image-with-text':
      return (
        <section className={`landing-image-text ${s.layout === 'text_first' ? 'landing-image-text--reversed' : ''}`}>
          <div className="landing-container landing-image-text-grid">
            {s.image_url && <img src={String(s.image_url)} alt={heading} className="landing-image-text-img" />}
            <div className="landing-image-text-content">
              {heading && <h2>{heading}</h2>}
              {content && <div dangerouslySetInnerHTML={{ __html: content }} />}
            </div>
          </div>
        </section>
      );

    case 'image-banner':
      return (
        <section className="landing-image-banner">
          {s.image_url && <img src={String(s.image_url)} alt={heading} className="landing-banner-img" />}
          {heading && <div className="landing-banner-overlay"><h2>{heading}</h2></div>}
        </section>
      );

    case 'video':
      return (
        <section className="landing-video">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            {s.video_url && (
              <div className="landing-video-embed">
                <iframe
                  src={getEmbedUrl(String(s.video_url))}
                  title={heading || 'Video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </section>
      );

    case 'countdown':
      return (
        <section className="landing-countdown">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <CountdownTimer endDate={String(s.end_date ?? '')} />
          </div>
        </section>
      );

    case 'testimonials':
      return (
        <section className="landing-testimonials">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <p className="landing-placeholder">Testimonios configurables desde el editor</p>
          </div>
        </section>
      );

    case 'multicolumn':
      return (
        <section className="landing-multicolumn">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <div className={`landing-columns landing-columns--${s.columns ?? 3}`}>
              {Array.from({ length: Number(s.columns ?? 3) }).map((_, i) => (
                <div key={i} className="landing-column">
                  <h3>{String(s[`col_${i}_title`] ?? `Columna ${i + 1}`)}</h3>
                  <p>{String(s[`col_${i}_text`] ?? 'Descripción')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'newsletter':
      return (
        <section className="landing-newsletter">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <p className="landing-placeholder">Suscripción por email</p>
          </div>
        </section>
      );

    case 'logo-list':
      return (
        <section className="landing-logos">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <p className="landing-placeholder">Logos de clientes/partners</p>
          </div>
        </section>
      );

    case 'collapsible-content':
      return (
        <section className="landing-faq">
          <div className="landing-container">
            {heading && <h2>{heading}</h2>}
            <p className="landing-placeholder">FAQ / Acordeón</p>
          </div>
        </section>
      );

    case 'section-divider':
      return <hr className="landing-divider" />;

    case 'custom-liquid':
      return (
        <section className="landing-custom">
          <div className="landing-container">
            {s.html && <div dangerouslySetInnerHTML={{ __html: String(s.html) }} />}
          </div>
        </section>
      );

    default:
      return (
        <section className="landing-section-unknown">
          <div className="landing-container">
            <p>Sección: {section.section_type}</p>
          </div>
        </section>
      );
  }
}

// ─── Countdown Timer ─────────────────────────────────────
function CountdownTimer({ endDate }: { endDate: string }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!endDate) return;

    const target = new Date(endDate).getTime();
    const update = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  return (
    <div className="landing-countdown-timer">
      <div className="landing-countdown-unit"><span>{timeLeft.days}</span><small>Días</small></div>
      <div className="landing-countdown-unit"><span>{timeLeft.hours}</span><small>Horas</small></div>
      <div className="landing-countdown-unit"><span>{timeLeft.minutes}</span><small>Min</small></div>
      <div className="landing-countdown-unit"><span>{timeLeft.seconds}</span><small>Seg</small></div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────
function getEmbedUrl(url: string): string {
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return url;
}
